import i18n from "../i18n";
import type { Campaign, Run } from "../api";
import { newFailures, onlyKnown } from "./knownProblems";

export type BuildTone = "good" | "warn" | "bad" | "off" | "live";

/** A project's build as GET /projects/{id}/builds sends it. */
export type ProjectBuild = {
  key: string;
  label: string;
  first_at: number | null;
  last_at: number | null;
  runs: Run[];
  campaigns: Campaign[];
};

export function failedOf(r: Run): number {
  const v = r.verdict_count ?? 0;
  if (v > 0) return Math.max(0, v - (r.v_passed ?? 0) - (r.v_xfailed ?? 0) - (r.v_skipped ?? 0));
  return r.failed_scenarios ?? 0;
}

/**
 * What a build's newest result says: a word for its row and a sentence for
 * its page. The newest campaign decides; without one, the newest FULL run;
 * without that, the newest run - a slice says only what its cases say.
 */
export function buildVerdict(b: ProjectBuild): { tone: BuildTone; word: string; answer: string } {
  const t = i18n.t.bind(i18n);
  const c = b.campaigns[0];
  if (c) {
    if (c.status === "running") return { tone: "live", word: t("builds.wRunning"), answer: t("builds.aRunning") };
    if (c.status === "green") return { tone: "good", word: t("builds.wGreen"), answer: t("builds.aGreen") };
    if (onlyKnown(c)) return { tone: "warn", word: t("builds.wKnown"), answer: t("builds.aKnown", { n: c.known }) };
    const fresh = newFailures(c);
    if (fresh) {
      return {
        tone: "bad",
        word: t("builds.wFailed", { count: fresh }),
        answer: c.known
          ? `${t("builds.aFailed", { count: fresh })} ${t("known.also", { n: c.known })}`
          : t("builds.aFailed", { count: fresh }),
      };
    }
    if (c.lost) return { tone: "bad", word: t("builds.wLost"), answer: t("builds.aLost") };
    return { tone: "warn", word: t("builds.wShort"), answer: t("builds.aShort") };
  }
  const run = b.runs.find((r) => r.scope === "full") ?? b.runs[0];
  if (!run) return { tone: "off", word: t("builds.wNone"), answer: t("builds.aNone") };
  const f = failedOf(run);
  return f
    ? { tone: "bad", word: t("builds.wFailed", { count: f }), answer: t("builds.aRunFailed", { run: run.name, count: f }) }
    : { tone: "good", word: t("builds.wClean"), answer: t("builds.aRunClean", { run: run.name }) };
}
